import {
  DocumentRelationType,
  DocumentStatus,
  DocumentType,
  DocumentVisibilityScope,
  type CompleteUploadResponse,
  type CreateDocumentLinkRequest,
  type CreatedApiResponse,
  type DeletePropertyDocumentResponse,
  type DocumentLink,
  type DocumentTaxonomyResponse,
  type DownloadDocumentUrlResponse,
  type EntityDocumentLinkResponse,
  type GetDocumentResponse,
  type PropertyDocument,
  type UpdatePropertyDocumentRequest,
  type UploadIntentData,
} from '@/domain/entities/property-document';
import { getApiEndpoints } from '@/services/api/endpoints';
import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import type {
  EntityDocumentParams,
  ListPropertyDocumentsParams,
  PropertyDocumentParams,
  PropertyScopedParams,
  UploadLinkedPropertyDocumentParams,
  UploadPropertyDocumentParams,
} from './propertyDocumentsTypes';

type ApiError = { message: string };

function vaultBasePath(): string {
  return getApiEndpoints().core.propertyDocumentsVault.base;
}

function propertyDocumentsPath(propertyID: string): string {
  return `${vaultBasePath()}/${encodeURIComponent(propertyID)}/documents`;
}

function propertyDocumentPath(propertyID: string, documentID: string): string {
  return `${propertyDocumentsPath(propertyID)}/${encodeURIComponent(documentID)}`;
}

function entityDocumentsPath({ propertyID, entityType, entityID }: EntityDocumentParams): string {
  return `${vaultBasePath()}/${encodeURIComponent(propertyID)}/entities/${encodeURIComponent(entityType)}/${encodeURIComponent(
    entityID,
  )}/documents`;
}

function unwrapCreatedData<T>(response: CreatedApiResponse<T> | T): T {
  if (response && typeof response === 'object' && 'data' in response) {
    return (response as CreatedApiResponse<T>).data;
  }
  return response as T;
}

function getFileName(file: File): string {
  return file.name?.trim() || 'document';
}

function getMimeType(file: File): string {
  return file.type?.trim() || 'application/octet-stream';
}

async function putFileToUploadUrl(args: { uploadUrl: string; file: File; mimeType: string }): Promise<void> {
  const response = await fetch(args.uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': args.mimeType,
    },
    body: args.file,
  });

  if (!response.ok) {
    throw new Error(`S3 upload failed: HTTP ${response.status}`);
  }
}

function toRejectValue(error: unknown, fallback: string): ApiError {
  return { message: error instanceof Error && error.message ? error.message : fallback };
}

export const fetchPropertyDocumentTaxonomy = createAppAsyncThunk<
  DocumentTaxonomyResponse,
  PropertyScopedParams & { includeCounts?: boolean }
>(
  'propertyDocuments/fetchTaxonomy',
  async ({ propertyID, includeCounts = true }, { extra: { config }, rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ includeCounts: String(includeCounts) });
      const endpoint = `${propertyDocumentsPath(propertyID)}/taxonomy?${params.toString()}`;
      return await config.apiService.get<DocumentTaxonomyResponse>(endpoint);
    } catch (error) {
      return rejectWithValue(toRejectValue(error, 'Failed to fetch document taxonomy'));
    }
  },
);

export const listPropertyDocuments = createAppAsyncThunk<PropertyDocument[], ListPropertyDocumentsParams>(
  'propertyDocuments/list',
  async ({ propertyID, filters = {} }, { extra: { config }, rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (filters.category) params.set('category', filters.category);
      if (filters.subcategory) params.set('subcategory', filters.subcategory);
      if (filters.status) params.set('status', filters.status);

      const query = params.toString();
      const endpoint = query ? `${propertyDocumentsPath(propertyID)}?${query}` : propertyDocumentsPath(propertyID);
      return await config.apiService.get<PropertyDocument[]>(endpoint);
    } catch (error) {
      return rejectWithValue(toRejectValue(error, 'Failed to list property documents'));
    }
  },
);

export const listEntityPropertyDocuments = createAppAsyncThunk<PropertyDocument[], EntityDocumentParams>(
  'propertyDocuments/listEntity',
  async (params, { extra: { config }, rejectWithValue }) => {
    try {
      const response = await config.apiService.get<EntityDocumentLinkResponse[] | PropertyDocument[]>(
        entityDocumentsPath(params),
      );

      return response
        .map((entry) => ('document' in entry ? entry.document : entry))
        .filter((document): document is PropertyDocument => Boolean(document));
    } catch (error) {
      return rejectWithValue(toRejectValue(error, 'Failed to list entity documents'));
    }
  },
);

export const fetchPropertyDocument = createAppAsyncThunk<GetDocumentResponse, PropertyDocumentParams>(
  'propertyDocuments/fetchOne',
  async ({ propertyID, documentID }, { extra: { config }, rejectWithValue }) => {
    try {
      return await config.apiService.get<GetDocumentResponse>(propertyDocumentPath(propertyID, documentID));
    } catch (error) {
      return rejectWithValue(toRejectValue(error, 'Failed to fetch property document'));
    }
  },
);

export const createAndUploadPropertyDocument = createAppAsyncThunk<
  CompleteUploadResponse,
  UploadPropertyDocumentParams
>(
  'propertyDocuments/createAndUpload',
  async ({ propertyID, metadata, file }, { extra: { config }, rejectWithValue }) => {
    try {
      const mimeType = getMimeType(file);
      const createdDocumentResponse = await config.apiService.post<
        CreatedApiResponse<PropertyDocument> | PropertyDocument
      >(propertyDocumentsPath(propertyID), {
        ...metadata,
        documentType: metadata.documentType ?? DocumentType.File,
        status: metadata.status ?? DocumentStatus.Draft,
        visibilityScope: metadata.visibilityScope ?? DocumentVisibilityScope.Private,
        tags: metadata.tags ?? [],
      });
      const createdDocument = unwrapCreatedData(createdDocumentResponse);
      const uploadIntentResponse = await config.apiService.post<
        CreatedApiResponse<UploadIntentData> | UploadIntentData
      >(`${propertyDocumentPath(propertyID, createdDocument.documentID)}/upload-intent`, {
        fileName: getFileName(file),
        mimeType,
        sizeBytes: file.size,
      });
      const uploadIntent = unwrapCreatedData(uploadIntentResponse);

      await putFileToUploadUrl({ uploadUrl: uploadIntent.uploadUrl, file, mimeType });

      return await config.apiService.post<CompleteUploadResponse>(
        `${propertyDocumentPath(propertyID, createdDocument.documentID)}/complete-upload`,
        {
          versionID: uploadIntent.version.versionID,
          versionNumber: uploadIntent.version.versionNumber,
        },
      );
    } catch (error) {
      return rejectWithValue(toRejectValue(error, 'Failed to upload property document'));
    }
  },
);

export const createAndUploadLinkedPropertyDocument = createAppAsyncThunk<
  CompleteUploadResponse & { link: DocumentLink },
  UploadLinkedPropertyDocumentParams
>(
  'propertyDocuments/createAndUploadLinked',
  async ({ entityType, entityID, relationType = DocumentRelationType.Supporting, ...upload }, thunkApi) => {
    const {
      extra: { config },
      rejectWithValue,
    } = thunkApi;

    try {
      const completed = await thunkApi.dispatch(createAndUploadPropertyDocument(upload)).unwrap();
      const linkResponse = await config.apiService.post<CreatedApiResponse<DocumentLink> | DocumentLink>(
        `${propertyDocumentPath(upload.propertyID, completed.document.documentID)}/links`,
        {
          entityType,
          entityID,
          relationType,
        } satisfies CreateDocumentLinkRequest,
      );

      return {
        ...completed,
        link: unwrapCreatedData(linkResponse),
      };
    } catch (error) {
      return rejectWithValue(toRejectValue(error, 'Failed to upload linked property document'));
    }
  },
);

export const replacePropertyDocumentFile = createAppAsyncThunk<
  CompleteUploadResponse,
  PropertyDocumentParams & { file: File }
>('propertyDocuments/replaceFile', async ({ propertyID, documentID, file }, { extra: { config }, rejectWithValue }) => {
  try {
    const mimeType = getMimeType(file);
    const uploadIntentResponse = await config.apiService.post<CreatedApiResponse<UploadIntentData> | UploadIntentData>(
      `${propertyDocumentPath(propertyID, documentID)}/upload-intent`,
      {
        fileName: getFileName(file),
        mimeType,
        sizeBytes: file.size,
      },
    );
    const uploadIntent = unwrapCreatedData(uploadIntentResponse);

    await putFileToUploadUrl({ uploadUrl: uploadIntent.uploadUrl, file, mimeType });

    return await config.apiService.post<CompleteUploadResponse>(
      `${propertyDocumentPath(propertyID, documentID)}/complete-upload`,
      {
        versionID: uploadIntent.version.versionID,
        versionNumber: uploadIntent.version.versionNumber,
      },
    );
  } catch (error) {
    return rejectWithValue(toRejectValue(error, 'Failed to replace property document file'));
  }
});

export const fetchPropertyDocumentDownloadUrl = createAppAsyncThunk<
  DownloadDocumentUrlResponse,
  PropertyDocumentParams
>('propertyDocuments/fetchDownloadUrl', async ({ propertyID, documentID }, { extra: { config }, rejectWithValue }) => {
  try {
    return await config.apiService.get<DownloadDocumentUrlResponse>(
      `${propertyDocumentPath(propertyID, documentID)}/download-url`,
    );
  } catch (error) {
    return rejectWithValue(toRejectValue(error, 'Failed to fetch document download URL'));
  }
});

export const updatePropertyDocumentMetadata = createAppAsyncThunk<
  PropertyDocument,
  PropertyDocumentParams & { metadata: UpdatePropertyDocumentRequest }
>(
  'propertyDocuments/updateMetadata',
  async ({ propertyID, documentID, metadata }, { extra: { config }, rejectWithValue }) => {
    try {
      return await config.apiService.patch<PropertyDocument>(propertyDocumentPath(propertyID, documentID), metadata);
    } catch (error) {
      return rejectWithValue(toRejectValue(error, 'Failed to update property document'));
    }
  },
);

export const archivePropertyDocument = createAppAsyncThunk<PropertyDocument, PropertyDocumentParams>(
  'propertyDocuments/archive',
  async ({ propertyID, documentID }, { extra: { config }, rejectWithValue }) => {
    try {
      return await config.apiService.post<PropertyDocument>(`${propertyDocumentPath(propertyID, documentID)}/archive`);
    } catch (error) {
      return rejectWithValue(toRejectValue(error, 'Failed to archive property document'));
    }
  },
);

export const deletePropertyDocument = createAppAsyncThunk<DeletePropertyDocumentResponse, PropertyDocumentParams>(
  'propertyDocuments/delete',
  async ({ propertyID, documentID }, { extra: { config }, rejectWithValue }) => {
    try {
      const response = await config.apiService.delete<
        CreatedApiResponse<DeletePropertyDocumentResponse> | DeletePropertyDocumentResponse
      >(propertyDocumentPath(propertyID, documentID));
      return unwrapCreatedData(response);
    } catch (error) {
      return rejectWithValue(toRejectValue(error, 'Failed to delete property document'));
    }
  },
);
